import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useStores } from '@/hooks/use-stores';
import { useStage } from '../Stage/Provider';

dayjs.extend(duration);

export interface TimeToCompleteDetails {
  timerString: string;
  isWithin24Hours: boolean;
  isPeriodPassed: boolean;
}

export const useStageTimer = () => {
  const intervalRef = useRef<NodeJS.Timeout>();
  const { t } = useTranslation();
  const { isCurrent, startTime, expectedDuration } = useStage();
  const { lifecycleStore } = useStores();

  const calculateTimeToComplete = useCallback((from: dayjs.Dayjs, to: dayjs.Dayjs) => {
    const timeLeft = to.diff(from);
    const timeLeftDuration = dayjs.duration(Math.abs(timeLeft));
    const durationInDays = Math.floor(timeLeftDuration.asDays());
    const hoursDuration = timeLeftDuration.subtract(durationInDays, 'days');

    const isPeriodPassed = timeLeft < 0;
    const isWithin24Hours = durationInDays < 1;

    const hoursString = dayjs
      .duration({
        hours: hoursDuration.hours(),
        minutes: hoursDuration.minutes(),
        seconds: hoursDuration.seconds(),
      })
      .format('HH:mm');

    const daysString = durationInDays > 0 ? `${durationInDays}${t('dayChar')} ` : '';

    return {
      timerString: daysString + hoursString,
      isWithin24Hours,
      isPeriodPassed,
    };
  }, []);

  const updateTimeToComplete = useCallback(() => {
    const newDetails = calculateTimeToComplete(dayjs(), dayjs(startTime).add(expectedDuration, 'days'));
    lifecycleStore.updateStageTimer(newDetails);
  }, [startTime, expectedDuration]);

  useEffect(() => {
    if (isCurrent) {
      intervalRef.current = setInterval(updateTimeToComplete, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isCurrent]);
};
